import { useEffect, type RefObject } from 'react';

type ElementRef = RefObject<HTMLElement | null>;

/** Portaled bubble of a `TooltipTrigger` rendered with `interactive`. */
const INTERACTIVE_TOOLTIP_SELECTOR = '.spark-tooltip-trigger-popup--interactive';

/**
 * Calls `onOutsideClick` when a click occurs outside the referenced element(s).
 *
 * Accepts a single ref or an array of refs. A click is considered "inside" when
 * it lands within any of the provided elements — useful when part of a component
 * (e.g. a portaled dropdown panel) lives outside the root element in the DOM.
 *
 * Clicks inside an interactive `TooltipTrigger` bubble are also treated as
 * inside: the bubble is portaled to `document.body` but belongs to whatever
 * element inside the component is being hovered, so a link in it must not
 * dismiss the component it explains.
 *
 * Only active when `enabled` is true (default).
 */
export function useOutsideClick(
  refs: ElementRef | ElementRef[],
  onOutsideClick: () => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const refList = Array.isArray(refs) ? refs : [refs];
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      const clickedInside =
        refList.some((ref) => ref.current?.contains(target)) ||
        (target instanceof Element && target.closest(INTERACTIVE_TOOLTIP_SELECTOR) !== null);
      if (!clickedInside) {
        onOutsideClick();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onOutsideClick, enabled, ...(Array.isArray(refs) ? refs : [refs])]);
}
